import { cloneDeep } from '@studio/utils/clone';
import { CRC32checksum } from '@studio/utils/utils';
import { toFlatNodeList } from './nodes/helpers';
import { deserializeDesignDocument, stringifyCreativeDataNode } from './serialization/document-serializer';
import { deserializeVersionProperties } from './serialization/property-value-serializer';
export function getVersionIdsFromCreatives(creatives) {
    return [...new Set(creatives.map(creative => creative.version.id))];
}
export function getSizeIdsFromCreatives(creatives) {
    return [...new Set(creatives.map(creative => creative.size.id))];
}
export function adjustWeightCalculation(creativeWeights) {
    if (!creativeWeights.weights) {
        return creativeWeights;
    }
    const { initialLoad, subLoad, url, frameworkOverhead } = creativeWeights.weights;
    const subLoadAssets = (subLoad === null || subLoad === void 0 ? void 0 : subLoad.assets) || [];
    const filteredInitialLoadAssets = cloneDeep(subLoadAssets.filter(asset => asset.name === 'ad.js' || asset.name === 'preload.jpg'));
    const filteredSubLoadAssets = cloneDeep(subLoadAssets.filter(asset => asset.name !== 'ad.js' && asset.name !== 'preload.jpg'));
    const initialLoadTotalWeight = filteredInitialLoadAssets.reduce((acc, curr) => acc + curr.weight, 0);
    const subLoadTotalWeight = filteredSubLoadAssets.reduce((acc, curr) => acc + curr.weight, 0);
    return Object.assign(Object.assign({}, creativeWeights), { weights: {
            url,
            frameworkOverhead,
            initialLoad: Object.assign(Object.assign({}, initialLoad), { assets: filteredInitialLoadAssets, totalWeight: initialLoadTotalWeight }),
            subLoad: Object.assign(Object.assign({}, subLoad), { assets: filteredSubLoadAssets, totalWeight: subLoadTotalWeight }),
            totalWeight: ((initialLoad === null || initialLoad === void 0 ? void 0 : initialLoad.totalWeight) || 0) + ((subLoad === null || subLoad === void 0 ? void 0 : subLoad.totalWeight) || 0)
        } });
}
export function creativeToChecksum(creative, version, stateId) {
    const { id, design, targetUrl, size } = creative;
    const creativeHasDesign = !!(design === null || design === void 0 ? void 0 : design.elements);
    if (creativeHasDesign && typeof design.document === 'string') {
        design.document = deserializeDesignDocument(design.document);
    }
    const elements = !creativeHasDesign
        ? undefined
        : design.elements.filter(element => toFlatNodeList(design.document).find(e => e.id === element.id));
    const filteredVersionProperties = !elements
        ? []
        : version.properties.filter(property => !!elements.find(element => !!element.properties.find(elProp => elProp.versionPropertyId === property.id)));
    // Serialize since any 'Map' gets stringified as '{}' regardless its content
    const document = design ? JSON.parse(stringifyCreativeDataNode(design.document)) : undefined;
    const stringified = JSON.stringify({
        id,
        document,
        elements: elements,
        size,
        version: deserializeVersionProperties(filteredVersionProperties),
        targetUrl,
        versionTargetUrl: version.targetUrl,
        stateId: stateId || undefined
    });
    return CRC32checksum(stringified);
}
export function updateChecksumOfCreatives(dirtyCreatives, dirtyVersions, stateId) {
    return dirtyCreatives.reduce((results, creative) => {
        const version = dirtyVersions.find(v => v.id === creative.version.id);
        const newChecksum = creativeToChecksum(creative, version, stateId);
        if (newChecksum !== creative.checksum) {
            creative.checksum = newChecksum;
            results.push(creative);
        }
        return results;
    }, []);
}
/**
 * Get the nearest scale to aspect ratio for a creative element within a container.
 * It makes sure the returned scale maintains the aspect ratio of the creative element as much as possible.
 * @param creativeSize The size of the creative element.
 * @param containerSize The size of the container.
 * @param isResponsiveContain Whether the responsive contain mode is enabled. Default is true.
 * @returns The scale factor to apply to the creative element.
 */
export function getNearestScaleToAspectRatio(creativeSize, containerSize, isResponsiveContain = true) {
    const widthRatio = containerSize.width / creativeSize.width;
    const heightRatio = containerSize.height / creativeSize.height;
    const initialScale = Math.max(0.01, isResponsiveContain ? Math.min(widthRatio, heightRatio) : widthRatio);
    const aspectRatio = creativeSize.width / creativeSize.height;
    const widthOnScale = Math.ceil(creativeSize.width * initialScale);
    const heightOnScale = Math.ceil(creativeSize.height * initialScale);
    const heightOnAspectRatio = Math.ceil(widthOnScale / aspectRatio);
    const widthOnAspectRatio = Math.ceil(heightOnScale * aspectRatio);
    const scalePrecision = Math.max(Math.abs(widthOnScale - widthOnAspectRatio) / creativeSize.width, Math.abs(heightOnScale - heightOnAspectRatio) / creativeSize.height);
    const width = Math.min(widthOnScale, widthOnAspectRatio);
    const height = Math.min(heightOnScale, heightOnAspectRatio);
    const scale = Math.max(width / creativeSize.width, height / creativeSize.height) + scalePrecision;
    return scale;
}
